import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const lineKey = (productId, variantId) => (variantId ? `${productId}:${variantId}` : productId);

const emptyDraft = {
  customer_name: "",
  customer_phone: "",
  delivery_address: "",
  due_date: "",
  notes: "",
  is_preorder: false,
  source_text: "",
};

const Ctx = createContext(null);

export function CartProvider({ children }) {
  const [lines, setLines] = useState([]);
  const [draft, setDraftState] = useState(emptyDraft);

  const add = useCallback((product, qty = 1, variant = null) => {
    const key = lineKey(product.id, variant?.id);
    const price = variant ? (variant.effective_price ?? variant.price ?? product.price) : product.price;
    const name = variant ? `${product.name} · ${variant.name}` : product.name;
    setLines((prev) => {
      const i = prev.findIndex((l) => l.key === key);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: next[i].qty + qty };
        return next;
      }
      return [...prev, { key, product_id: product.id, variant_id: variant?.id || null, name, price, qty, product, variant, note: "" }];
    });
  }, []);

  const setQty = useCallback((key, qty) => {
    setLines((prev) => (qty <= 0 ? prev.filter((l) => l.key !== key) : prev.map((l) => (l.key === key ? { ...l, qty } : l))));
  }, []);

  const remove = useCallback((key) => setLines((prev) => prev.filter((l) => l.key !== key)), []);
  const clear = useCallback(() => { setLines([]); setDraftState(emptyDraft); }, []);
  const setDraft = useCallback((d) => setDraftState((prev) => ({ ...prev, ...d })), []);

  const value = useMemo(
    () => ({
      lines,
      draft,
      add,
      setQty,
      remove,
      clear,
      setDraft,
      subtotal: lines.reduce((s, l) => s + l.qty * l.price, 0),
      count: lines.reduce((s, l) => s + l.qty, 0),
    }),
    [lines, draft, add, setQty, remove, clear, setDraft]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCart outside CartProvider");
  return v;
}
